import React, { useCallback, useEffect, useState, useImperativeHandle, forwardRef, useRef } from 'react';
import EmblaCarouselProps, { EmblaCarouselHandle } from './props';
import style from './style.module.scss';
import classNames from '../../../utils/classNames';
import config from './config.json';
import EmblaCarouselDots from './components/EmblaCarouselDots/EmblaCarouselDots';
import {definedValue} from '../../../utils/input';

import useEmblaCarousel from 'embla-carousel-react';
import {
  EmblaEventType,
  EmblaOptionsType,
} from 'embla-carousel';

import Button from '../../private/Button/Button';
import Image from '../../private/Image';

import arrow_left from '../../../icons/arrow_left.svg';
import arrow_right from '../../../icons/arrow_right.svg';
import defaultImage from '../../../icons/placeholder-image3x2.svg';

const EmblaCarousel = forwardRef<EmblaCarouselHandle, EmblaCarouselProps>((props, ref) => {
  const options: EmblaOptionsType = { 
    loop: true,
    watchDrag: props.draggable !== false
  };
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselContainerRef = useRef<HTMLDivElement>(null);

  const { selectedIndex, setSelectedIndex, onSlideChange } = props;

  const onPrevButtonClick = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
      setCurrentIndex(emblaApi.selectedScrollSnap());
    }
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
      setCurrentIndex(emblaApi.selectedScrollSnap());
    }
  }, [emblaApi]);

  // Expose methods through ref
  useImperativeHandle(ref, () => ({
    goToNext: () => {
      if (emblaApi) {
        emblaApi.scrollNext();
        setCurrentIndex(emblaApi.selectedScrollSnap());
      }
    },
    goToPrevious: () => {
      if (emblaApi) {
        emblaApi.scrollPrev();
        setCurrentIndex(emblaApi.selectedScrollSnap());
      }
    },
    focus: () => {
      carouselContainerRef.current?.focus();
    }
  }), [emblaApi]);

  const goToSlide = useCallback(() => {
    if (emblaApi && selectedIndex !== undefined) {
      emblaApi.scrollTo(selectedIndex, true);
    }
  }, [emblaApi, selectedIndex]);

  const handleItemClick: (
    e: React.MouseEvent<HTMLElement>,
    index: number
  ) => void = (e: React.MouseEvent<HTMLElement>, index: number) => {
    if (props.handleCarouselItemClick) {
      props.handleCarouselItemClick(e, index);
    }
  };

  useEffect(() => {
    if (!onSlideChange) {
      if (emblaApi) {
        emblaApi.on('select' as EmblaEventType, () => {
          setSelectedIndex?.(emblaApi.selectedScrollSnap());
        });
      }
      props.goToSlide && goToSlide();
    }
  });

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on('select' as EmblaEventType, () => {
        onSlideChange && onSlideChange();
        setSelectedIndex?.(emblaApi.selectedScrollSnap());
      });
    }
    props.goToSlide && goToSlide();
  },[emblaApi, goToSlide, onSlideChange, props.goToSlide, setSelectedIndex]);

  const leftButtonProps = (props: EmblaCarouselProps)  => {
    return {
      classNames: {
        ...config.Button.classNames,
      },
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        onPrevButtonClick();
        if (props.prevOnClick) {
          props.prevOnClick(event);
        }
      },
      Icon: {
        svg: arrow_left,
        alt: props.prevIconAlt || 'Previous Slide',
      },
    };
  };
  const rightButtonProps = (props: EmblaCarouselProps) => {
    return {
      classNames: {
        ...config.Button.classNames,
      },
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        onNextButtonClick();
        if (props.nextOnClick) {
          props.nextOnClick(event);
        }
      },
      Icon: {
        svg: arrow_right,
        alt: props.nextIconAlt || 'Next Slide',
      },
    };
  };

  const showArrowButtons = React.Children.count(props.children) > 1;
  const showDefaultImage = React.Children.count(props.children) < 1;
  const showIndicators = props.classNames?.withIndicators && React.Children.count(props.children) > 1;
  const defaultNoImages = (
    <Image
      src={defaultImage}
      alt='No Image'
      defaultImage={defaultImage}
      key='no image'
    />
  );
  const customClass = definedValue(props?.carouselClassName, '');
  const className = `embla-carousel ${classNames('emblaCarousel', style, props.classNames)} ${customClass}`;

  return (
    <div
      className={className}
      data-e2e='embla-carousel'
      ref={carouselContainerRef}
      tabIndex={0}
      role="region"
      aria-label="Image carousel"
    >
      <div className='embla' ref={emblaRef}>
        <div className='embla__container'>
          {showDefaultImage && defaultNoImages}
          {props.children.map((child, index) => (
            <div
              className='embla__slide'
              key={index}
              onClick={(e) => handleItemClick(e, index)}
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      {showIndicators && (
        <EmblaCarouselDots activeSlideIndex={currentIndex} childrenLength={props.children.length} />
      )}

      {showArrowButtons && !props.hideArrowButtons && (
        <>
          <Button {...leftButtonProps(props)} role='prev'></Button>
          <Button {...rightButtonProps(props)} role='next'></Button>
        </>
      )}
      {/* {showArrowButtons && (
        <Button {...rightButtonProps(props)} role='next'></Button>
      )} */}
    </div>
  );
});

EmblaCarousel.displayName = 'EmblaCarousel';

export default React.memo(EmblaCarousel);
